import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import EntityOfficerInvitation from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import SystemNotification from "notifications/system-notification/system-notification";
import Session from "users/__session/session";
import { AuthResponse } from "users/_api/auth-email-api-service";
import { AuthInfo } from "users/auth/info/auth-info";
import AuthInfoSchema from "users/auth/info/auth-info-schema";
import { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import UserInformation from "users/information/user-information";
import InviteEntityOfficerAPIRequest from "users/super-users/_api/commands/invite-entity-officer-api-request";
import SearchUsersAPIRequest from "users/super-users/_api/commands/search-users-api-request";
import SystemNotificationAPIResponse from "users/super-users/_api/responses/system-notification-api-response";

export default class SuperUserAPIService {
  private headerWithJson(): AxiosHeaders {
    return this.authHeaders.concat({ "Content-Type": "application/json-patch+json" });
  }

  public constructor(session: Readonly<Session>) {
    if (!session.authToken?.value) throw new Error("Session must have an authToken to create a SuperUserAPIService");
    this.authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  public async register(userId: Guid): Promise<void> {
    const url = new URL("/super-user/register", AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        { userId: userId.value },
        {
          headers: this.headerWithJson(),
        },
      );
    } catch (error) {
      throw new SuperUserAPIServiceError("register", error);
    }
  }

  public async unregister(userId: Guid): Promise<void> {
    const url = new URL("/super-user/unregister", AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), { userId: userId.value }, { headers: this.headerWithJson() });
    } catch (error) {
      throw new SuperUserAPIServiceError("unregister", error);
    }
  }

  public async disableUser(userId: Guid): Promise<void> {
    const url = new URL("/super-user/disable-user", AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), { userId: userId.value }, { headers: this.headerWithJson() });
    } catch (error) {
      throw new SuperUserAPIServiceError("disableUser", error);
    }
  }

  public async enableUser(userId: Guid): Promise<void> {
    const url = new URL("/super-user/enable-user", AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), { userId: userId.value }, { headers: this.headerWithJson() });
    } catch (error) {
      throw new SuperUserAPIServiceError("enableUser", error);
    }
  }

  public async loginAsUser(userId: Guid): Promise<AuthResponse> {
    const url = new URL("/super-user/login-as-user", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        { userId: userId.value },
        {
          headers: this.headerWithJson(),
        },
      );

      return new AuthInfo(AuthInfoSchema.deserialize(response.data)).toAuthResponse();
    } catch (error) {
      throw new SuperUserAPIServiceError("loginAsUser", error);
    }
  }

  public async inviteEntityOfficer(invitation: EntityOfficerInvitation): Promise<EntityOfficerInvitation> {
    const url = new URL("/super-user/invite-entity-officer", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), new InviteEntityOfficerAPIRequest(invitation), {
        headers: this.headerWithJson(),
      });
      const updatedInvite = EntityOfficerInvitation.create({
        params: {
          ...invitation,
          id: new Guid(response.data.id),
        },
      });
      return updatedInvite;
    } catch (error) {
      throw new SuperUserAPIServiceError("inviteEntityOfficer", error);
    }
  }

  public async createSystemNotification(notificationType: string, notificationContent: string): Promise<void> {
    const url = new URL("/super-user/system-notifications", AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        { content: `${notificationContent}:${notificationType}` },
        { headers: this.headerWithJson() },
      );
    } catch (error) {
      throw new SuperUserAPIServiceError("createSystemNotification", error);
    }
  }

  public async deleteSystemNotification(notificationId: Guid) {
    const url = new URL(`/super-user/system-notifications/${notificationId}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(url.toString(), { headers: this.authHeaders });
    } catch (error) {
      throw new SuperUserAPIServiceError("deleteSystemNotification", error);
    }
  }

  public async searchUsers(
    request: SearchUsersAPIRequest,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<UserInformation>> {
    try {
      const url = request.toUrl("/super-user/search-users", AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      const data = response.data.map((item: UserInformationJSON) => UserInformation.fromJSON(item)).filter(Boolean);
      return PaginatedResponse.from<UserInformation>(data, response.headers["x-pagination"]);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new SuperUserAPIServiceError("searchUsers", error);
    }
  }

  public async getAppVersion(abortController?: AbortController): Promise<string> {
    const url = new URL("/super-user/version", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.Forbidden) {
        throw new VersionInfoNotAvailableError("Unable to fetch version info, caller must be a super-user");
      }
      if (error instanceof CanceledError) throw error;
      throw new SuperUserAPIServiceError("getAppVersion", error);
    }
  }

  public async getSystemNotifications(abortController?: AbortController): Promise<SystemNotification[]> {
    const url = new URL("/super-user/system-notifications", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      const systemNotifications: SystemNotification[] = [];
      for (const item of response.data as Partial<SystemNotificationAPIResponse>[]) {
        const systemNotification = new SystemNotificationAPIResponse(item);
        systemNotifications.push(systemNotification.deserialize());
      }
      return systemNotifications;
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.Forbidden) {
        throw new SystemNotificationsNotFoundError();
      }
      if (error instanceof CanceledError) throw error;
      throw new SuperUserAPIServiceError("getSystemNotifications", error);
    }
  }

  public async getActiveUserInvitations(abortController?: AbortController): Promise<UserEntityMemberInvitation[]> {
    const url = new URL("/super-user/active-user-invitations", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal,
      });
      return UserEntityMemberInvitation.fromJSONArray(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.NotFound) {
        throw new UserEntityMemberInvitationsNotFoundError();
      }
      if (error instanceof CanceledError) throw error;
      throw new SuperUserAPIServiceError("getActiveUserInvitations", error);
    }
  }

  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();
}

export class SuperUserAPIServiceError extends Error {
  constructor(method: string, error: unknown) {
    super(`Error in SuperUserAPIService.${method}: ${error}`);
  }
}
export class SystemNotificationsNotFoundError extends Error {
  constructor() {
    super(`Unable to fetch any system notifications`);
  }
}
export class UserEntityMemberInvitationsNotFoundError extends Error {
  constructor() {
    super(`Unable to retrieve open user entity member invitations`);
  }
}
export class VersionInfoNotAvailableError extends Error {}
